import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { XMarkIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { publishAppPackage, checkAppExists, type InstallContext, type DeviceRestartBehavior } from '../services/api'
import { errorText } from '../utils/errorText'
import { scrollPageToTop } from '../utils/pageScroll'

interface ExistingApp {
  id: string
  name: string
  current_version: string
}

export default function PublishApp() {
  const navigate = useNavigate()
  const [publishToIntune, setPublishToIntune] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [isChecking, setIsChecking] = useState(false)
  const [publishMessage, setPublishMessage] = useState<{ type: 'success' | 'error', message: string } | null>(null)
  const [existingApp, setExistingApp] = useState<ExistingApp | null>(null)
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false)

  const [formData, setFormData] = useState({
    app_name: '',
    app_description: '',
    version: '', // Read-only, populated from metadata.json
    publisher: '',
  })

  const [packageFile, setPackageFile] = useState<File | null>(null)

  // Install experience options (only relevant when publishing to Intune)
  const [installContext, setInstallContext] = useState<InstallContext>('system')
  const [deviceRestartBehavior, setDeviceRestartBehavior] = useState<DeviceRestartBehavior>('basedOnReturnCode')

  const handlePackageUpload = async (file: File) => {
    setPackageFile(file)
    setExistingApp(null)
    setShowUpdateConfirm(false)
    // TODO: Extract and read metadata.json from zip to auto-populate fields
  }

  const doPublish = async (isUpdate: boolean) => {
    setIsPublishing(true)
    setPublishMessage(null)
    setShowUpdateConfirm(false)

    try {
      const formDataObj = new FormData()
      formDataObj.append('package', packageFile!)
      // Backend expects deployment_method: "psadt", "intune", or "both"
      formDataObj.append('deployment_method', publishToIntune ? 'both' : 'psadt')
      formDataObj.append('app_name', formData.app_name)
      formDataObj.append('app_description', formData.app_description)
      formDataObj.append('version', formData.version)
      formDataObj.append('publisher', formData.publisher)
      formDataObj.append('is_update', isUpdate.toString())
      if (isUpdate && existingApp) {
        formDataObj.append('existing_app_id', existingApp.id)
      }
      // Include install experience options when publishing to Intune
      if (publishToIntune) {
        formDataObj.append('install_context', installContext)
        formDataObj.append('device_restart_behavior', deviceRestartBehavior)
      }

      await publishAppPackage(formDataObj)

      const action = isUpdate ? 'updated' : 'published'
      setPublishMessage({ type: 'success', message: `Application ${action} successfully! You can now view it in the app catalog.`})

      // Reset form after successful publish
      setPackageFile(null)
      setFormData({
        app_name: '',
        app_description: '',
        version: '',
        publisher: '',
      })
      setExistingApp(null)

      scrollPageToTop()
    } catch (error: any) {
      setPublishMessage({
        type: 'error',
        message: `Failed to publish application: ${errorText(error)}`
      })
      scrollPageToTop()
      console.error(error)
    } finally {
      setIsPublishing(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!packageFile) {
      setPublishMessage({ type: 'error', message: 'Please upload a package' })
      return
    }

    // Check if app exists before publishing
    setIsChecking(true)
    try {
      const result = await checkAppExists(formData.app_name)
      if (result.exists && result.app) {
        setExistingApp(result.app)
        setShowUpdateConfirm(true)
        setIsChecking(false)
        return // Wait for user confirmation
      }
    } catch (error) {
      console.error('Failed to check app existence:', error)
    } finally {
      setIsChecking(false)
    }

    // No existing app, proceed with new publish
    await doPublish(false)
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-theme-text-primary mb-8">Publish Application</h1>

      {/* Success/Error Message */}
      {publishMessage && (
        <div className={`mb-6 rounded-lg p-4 flex items-start justify-between ${
          publishMessage.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700'
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700'
        }`}>
          <div className="flex items-start">
            {publishMessage.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 mr-3 flex-shrink-0" />
            ) : (
              <ExclamationCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
            )}
            <p className={`text-sm font-medium ${
              publishMessage.type === 'success' ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'
            }`}>
              {publishMessage.message}
            </p>
          </div>
          <button
            onClick={() => setPublishMessage(null)}
            className={`flex-shrink-0 ml-4 ${
              publishMessage.type === 'success' ? 'text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300' : 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300'
            }`}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 bg-theme-card shadow rounded-lg p-6 border border-theme-border-primary">
        {/* AEAM Package Upload */}
        <div>
          <label htmlFor="package_file" className="block text-sm font-medium text-theme-text-primary mb-2">
            Package (ZIP) *
          </label>
          <input
            type="file"
            id="package_file"
            required
            accept=".zip"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) handlePackageUpload(file)
            }}
            className="mt-1 block w-full text-sm text-theme-text-secondary file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 dark:file:bg-blue-900/50 file:text-blue-700 dark:file:text-blue-400 hover:file:bg-blue-100 dark:hover:file:bg-blue-900/70"
          />
          <p className="mt-1 text-sm text-theme-text-tertiary">
            Upload the package Agentic Packr generated, containing the PSADT and Intune files
          </p>
        </div>

        {/* Publish to Intune Option */}
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="publish_to_intune"
              type="checkbox"
              checked={publishToIntune}
              onChange={(e) => setPublishToIntune(e.target.checked)}
              className="h-4 w-4 text-blue-600 border-theme-border-primary rounded focus:ring-blue-500"
            />
          </div>
          <div className="ml-3">
            <label htmlFor="publish_to_intune" className="text-sm font-medium text-theme-text-primary">
              Also publish to Microsoft Intune
            </label>
            <p className="text-sm text-theme-text-secondary">
              If checked, the application will also be uploaded to your organization's Microsoft Intune tenant for cloud deployment.
            </p>
          </div>
        </div>

        {/* Install Experience Options - shown when Intune publishing is enabled */}
        {publishToIntune && (
          <div className="ml-7 p-4 bg-theme-tertiary rounded-lg space-y-4 border border-theme-border-primary">
            <h3 className="text-sm font-medium text-theme-text-primary">Install Experience</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="install_context" className="block text-sm font-medium text-theme-text-secondary mb-1">
                  Install Context
                </label>
                <select
                  id="install_context"
                  value={installContext}
                  onChange={(e) => setInstallContext(e.target.value as InstallContext)}
                  className="w-full rounded-md border-theme-border-primary bg-theme-input text-theme-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="system">System (default)</option>
                  <option value="user">User</option>
                </select>
                <p className="mt-1 text-xs text-theme-text-tertiary">
                  System installs for all users; User installs for the logged-in user only
                </p>
              </div>
              <div>
                <label htmlFor="restart_behavior" className="block text-sm font-medium text-theme-text-secondary mb-1">
                  Restart Behavior
                </label>
                <select
                  id="restart_behavior"
                  value={deviceRestartBehavior}
                  onChange={(e) => setDeviceRestartBehavior(e.target.value as DeviceRestartBehavior)}
                  className="w-full rounded-md border-theme-border-primary bg-theme-input text-theme-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="basedOnReturnCode">Based on return code (default)</option>
                  <option value="allow">Allow restart</option>
                  <option value="suppress">Suppress restart</option>
                  <option value="force">Force restart</option>
                </select>
                <p className="mt-1 text-xs text-theme-text-tertiary">
                  Controls device restart behavior after installation
                </p>
              </div>
            </div>
          </div>
        )}

        {/* App Details - Auto-populated from metadata.json */}
        {packageFile && (
          <>
            <div>
              <label htmlFor="app_name" className="block text-sm font-medium text-theme-text-primary">
                Application Name
              </label>
              <input
                type="text"
                id="app_name"
                value={formData.app_name}
                onChange={(e) => setFormData({ ...formData, app_name: e.target.value })}
                placeholder="Will be auto-populated from metadata.json"
                className="mt-1 block w-full rounded-md border-theme-border-primary bg-theme-input text-theme-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="app_description" className="block text-sm font-medium text-theme-text-primary">
                Description
              </label>
              <textarea
                id="app_description"
                rows={3}
                value={formData.app_description}
                onChange={(e) => setFormData({ ...formData, app_description: e.target.value })}
                placeholder="Optional - add a description for your app"
                className="mt-1 block w-full rounded-md border-theme-border-primary bg-theme-input text-theme-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="version" className="block text-sm font-medium text-theme-text-primary">
                  Version <span className="text-theme-text-tertiary text-xs">(from metadata)</span>
                </label>
                <input
                  type="text"
                  id="version"
                  value={formData.version}
                  readOnly
                  placeholder="Auto-populated from metadata.json"
                  className="mt-1 block w-full rounded-md border-theme-border-primary bg-theme-tertiary shadow-sm text-theme-text-secondary cursor-not-allowed"
                />
              </div>

              <div>
                <label htmlFor="publisher" className="block text-sm font-medium text-theme-text-primary">
                  Publisher
                </label>
                <input
                  type="text"
                  id="publisher"
                  value={formData.publisher}
                  onChange={(e) => setFormData({ ...formData, publisher: e.target.value })}
                  placeholder="From metadata.json"
                  className="mt-1 block w-full rounded-md border-theme-border-primary bg-theme-input text-theme-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
          </>
        )}

        {/* Update Confirmation Dialog */}
        {showUpdateConfirm && existingApp && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
            <div className="flex items-start">
              <ExclamationCircleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  Application Already Exists
                </h3>
                <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                  An app named "<strong>{existingApp.name}</strong>" already exists with version <strong>{existingApp.current_version}</strong>.
                </p>
                <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                  Would you like to update it with version <strong>{formData.version}</strong>?
                </p>
                <div className="mt-4 flex space-x-3">
                  <button
                    type="button"
                    onClick={() => doPublish(true)}
                    disabled={isPublishing}
                    className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-md hover:bg-amber-700 disabled:opacity-50"
                  >
                    {isPublishing ? 'Updating...' : 'Yes, Update'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowUpdateConfirm(false)
                      setExistingApp(null)
                    }}
                    className="px-3 py-1.5 border border-amber-300 dark:border-amber-600 text-amber-700 dark:text-amber-400 text-sm rounded-md hover:bg-amber-100 dark:hover:bg-amber-900/30"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="px-4 py-2 border border-theme-border-primary rounded-md text-theme-text-secondary hover:bg-theme-tertiary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPublishing || isChecking}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isChecking ? 'Checking...' : isPublishing ? 'Publishing...' : 'Publish Application'}
          </button>
        </div>
      </form>
    </div>
  )
}
